import React, { useState } from 'react'
import { BiRefresh, BiSolidMagicWand } from 'react-icons/bi'
import TaskPlanSteps from './TaskPlanSteps'
import HotParamEditors from './HotParamEditors'
import { inputClass } from './taskScheduleConstants'
import type { PlanToolStep } from '../utils/taskPlanDisplay'

type Props = {
  mode: 'standalone' | 'chat_or_edit'
  toolsPipeline: PlanToolStep[]
  intentStatus: string
  intentFailed: boolean
  planAdjustmentText: string
  onPlanAdjustmentTextChange: (v: string) => void
  adjustAiExpanded: boolean
  onToggleAdjustAi: () => void
  onGeneratePlan: () => void
  onRegeneratePlan: () => void
  onChangeStepParam: (executionOrder: number, key: string, value: string) => void
  examplePrompts?: string[]
  onUseExamplePrompt?: (text: string) => void
}

const DEFAULT_EXAMPLES = [
  'Pull last week’s Google Ads spend by campaign',
  'Import order costs from my public Google Sheet and update order metadata',
  'Summarize website traffic for the last 7 days',
]

/** Plan preview, generate/adjust controls, hot params, and failed-synthesize recovery. */
export default function TaskConfigPlanSection({
  mode,
  toolsPipeline,
  intentStatus,
  intentFailed,
  planAdjustmentText,
  onPlanAdjustmentTextChange,
  adjustAiExpanded,
  onToggleAdjustAi,
  onGeneratePlan,
  onRegeneratePlan,
  onChangeStepParam,
  examplePrompts = DEFAULT_EXAMPLES,
  onUseExamplePrompt,
}: Props) {
  const loading = intentStatus === 'loading'
  const hasPlan = toolsPipeline.length > 0
  const [showExamples, setShowExamples] = useState(intentFailed)

  return (
    <div className="tw-space-y-3">
      {mode === 'standalone' && (
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <button
            type="button"
            disabled={loading}
            onClick={onGeneratePlan}
            className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 tw-transition-colors"
          >
            {loading ? (
              <span className="fa fa-spinner fa-spin tw-text-sm" aria-hidden />
            ) : hasPlan ? (
              <BiRefresh className="tw-h-4 tw-w-4" aria-hidden />
            ) : (
              <BiSolidMagicWand className="tw-h-4 tw-w-4 tw-text-blue-600" aria-hidden />
            )}
            {hasPlan ? 'Regenerate plan' : 'Generate plan'}
          </button>
        </div>
      )}

      {mode === 'chat_or_edit' && (
        <button
          type="button"
          onClick={onToggleAdjustAi}
          aria-expanded={adjustAiExpanded}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-1 tw-text-xs tw-font-medium tw-text-slate-600 hover:tw-border-slate-300 hover:tw-bg-slate-50 tw-transition-colors"
        >
          <BiSolidMagicWand className="tw-w-3.5 tw-h-3.5 tw-text-blue-600" aria-hidden />
          Adjust plan…
        </button>
      )}

      {((mode === 'chat_or_edit' && adjustAiExpanded) || (mode === 'standalone' && hasPlan)) && (
        <div className="tw-rounded-lg tw-border tw-border-blue-100 tw-bg-blue-50/80 tw-p-3 tw-space-y-2">
          <p className="tw-m-0 tw-text-xs tw-text-slate-600">
            {mode === 'standalone'
              ? 'Optionally describe how to change the plan, then apply the adjustment.'
              : 'Describe changes (add a step, different date range, different metrics).'}
          </p>
          <textarea
            value={planAdjustmentText}
            onChange={(e) => onPlanAdjustmentTextChange(e.target.value)}
            rows={3}
            className={`${inputClass} tw-resize-none tw-bg-white`}
            placeholder="e.g. Also break down results by campaign, or use last 30 days instead of 7."
            disabled={loading}
          />
          <button
            type="button"
            disabled={loading || !planAdjustmentText.trim()}
            onClick={onRegeneratePlan}
            className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-300 tw-bg-white tw-px-3.5 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 tw-transition-colors"
          >
            {loading ? (
              <span className="fa fa-spinner fa-spin tw-text-sm" aria-hidden />
            ) : (
              <BiRefresh className="tw-h-4 tw-w-4" aria-hidden />
            )}
            Apply adjustment
          </button>
        </div>
      )}

      {hasPlan && (
        <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-p-3 tw-space-y-3">
          <TaskPlanSteps steps={toolsPipeline} compact title="How this task runs" />
          <HotParamEditors steps={toolsPipeline} onChangeStepParam={onChangeStepParam} />
        </div>
      )}

      {intentFailed && (
        <div className="tw-rounded-lg tw-border tw-border-red-100 tw-bg-red-50/80 tw-p-3 tw-space-y-2">
          <p className="tw-m-0 tw-text-xs tw-text-red-700">
            Could not build a tool plan. Add more detail to the instructions, try an example, then generate again.
          </p>
          <button
            type="button"
            className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-[11px] tw-font-medium tw-text-red-700 tw-underline tw-cursor-pointer"
            onClick={() => setShowExamples((v) => !v)}
          >
            {showExamples ? 'Hide examples' : 'Show example prompts'}
          </button>
          {showExamples && onUseExamplePrompt && (
            <div className="tw-flex tw-flex-wrap tw-gap-1.5">
              {examplePrompts.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => onUseExamplePrompt(ex)}
                  className="tw-rounded-full tw-border tw-border-red-200 tw-bg-white tw-px-2.5 tw-py-1 tw-text-[11px] tw-text-slate-700 hover:tw-bg-red-50"
                >
                  {ex}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
